import { z } from 'zod';

/**
 * CNPJ validator implementation adapted from `cpf-cnpj-validator` library:
 * https://github.com/carvalhoviniciusluiz/cpf-cnpj-validator/blob/master/src/cnpj.ts
 */

// Blocklist common values.
const BLOCKLIST = [
  '00000000000000',
  '11111111111111',
  '22222222222222',
  '33333333333333',
  '44444444444444',
  '55555555555555',
  '66666666666666',
  '77777777777777',
  '88888888888888',
  '99999999999999',
];

const STRICT_STRIP_REGEX = /[-\\/.]/g;

const verifierDigit = (digits: string): number => {
  let index: number = 2;
  const reverse: Array<number> = digits.split('').reduce((buffer, number) => {
    return [parseInt(number, 10)].concat(buffer);
  }, []);

  const sum: number = reverse.reduce((buffer, number) => {
    buffer += number * index;
    index = index === 9 ? 2 : index + 1;
    return buffer;
  }, 0);

  const mod: number = sum % 11;
  return mod < 2 ? 0 : 11 - mod;
};

const isValid = (number: string): boolean => {
  const stripped = (number || '').replace(STRICT_STRIP_REGEX, '');

  // CNPJ must be defined
  if (!stripped) {
    return false;
  }

  // CNPJ must have 14 chars
  if (stripped.length !== 14) {
    return false;
  }

  // CNPJ can't be blacklisted
  if (BLOCKLIST.includes(stripped)) {
    return false;
  }

  let numbers: string = stripped.substr(0, 12);
  numbers += verifierDigit(numbers);
  numbers += verifierDigit(numbers);

  return numbers.substr(-2) === stripped.substr(-2);
};

type CnpjValidatorOptionsType = {
  messages?: Partial<{
    required: string;
    invalid: string;
  }>;
  path?: (string | number)[];
};

const cnpjValidator =
  ({ messages, path }: CnpjValidatorOptionsType = {}) =>
  (cnpj: string, ctx: z.RefinementCtx) => {
    if (!cnpj) {
      ctx.addIssue({
        code: 'custom',
        message: messages?.required || 'O CNPJ é obrigatório',
        path,
      });
      return;
    }

    if (!isValid(cnpj)) {
      ctx.addIssue({
        code: 'custom',
        message: messages?.invalid || 'O CNPJ está inválido',
        path,
      });
    }
  };

export default cnpjValidator;
